import React from 'react';
import Chip from '@material-ui/core/Chip';
import capitalize from 'lodash/capitalize';
import moment from 'moment';
import InfoLabelsGrid from 'UI/components/organisms/inventoryProfiles/InfoLabelsGrid';
import InventoryOverviewLayout from 'UI/components/organisms/inventoryProfiles/InventoryOverviewLayout';
import { DateFormats, YesNo } from 'UI/constants/defaults';
import { SharedFullGridLayout } from 'UI/constants/dimensions';
import { CANDIDATES_PROFILE_HASHES } from 'UI/constants/hashIds';
import { UIStatus } from 'UI/constants/status';
import { currencyFormatter, localTimeFormatter } from 'UI/utils';

import { useCandidateScrollToSection } from './CandidateOverview.hooks';
import { DefaultCards } from './components';
import {
  candidateBackgroundLabels,
  establishingUrgencyLabels,
  professionalInsights,
  workPreferencesLabels
} from './labels';
import { useStyles } from './styles';

const sharedWorkPreferencesConfig = {
  md: 4,
  sm: 4,
  xs: 6
};

/**
 * @param {Object} props
 * @param {Object} [props.profile]
 * @param {boolean} props.loading
 * @param {Object} [props.blueSheet]
 * @param {Function} props.onWriteUpClick
 * @param {Function} props.onEditClick
 */
const CandidateOverview = ({
  isProfileLoading,
  profile,
  onWriteUpClick,
  blueSheet,
  candidateDataSheet,
  candidateDataSheetStatus,
  onEditClick
}) => {
  const classes = useStyles();

  const loading = isProfileLoading || candidateDataSheetStatus === UIStatus.Loading;
  const success = candidateDataSheetStatus === UIStatus.Success;

  useCandidateScrollToSection({ success, loading });

  const writeUp = blueSheet ?? [{}];
  const lookingFor = writeUp?.time_looking && moment(writeUp?.time_looking).fromNow(true);

  const workPreferences = [
    {
      title: 'Open to relocation',
      description: capitalize(blueSheet?.relocation ? YesNo.Yes : YesNo.No),
      config: { ...sharedWorkPreferencesConfig }
    },
    {
      title: 'Preferred destinations',
      customValueRender: writeUp?.relocations?.map(item => {
        const chipLabel = item?.city?.is_state
          ? item?.city?.title
          : `${item?.city?.title}, ${item?.city?.state?.slug}`;
        return <Chip key={item.id} className={classes.writeUpChips} label={chipLabel} />;
      }),
      config: { ...sharedWorkPreferencesConfig }
    },
    {
      title: 'Work preference',
      description: writeUp?.workTypeOption?.title,
      config: { ...sharedWorkPreferencesConfig }
    },
    {
      title: 'High Compensation',
      description: currencyFormatter(writeUp?.good_salary),
      config: { ...sharedWorkPreferencesConfig }
    },
    {
      title: 'Ideal Compensation',
      description: currencyFormatter(writeUp?.no_brainer_salary),
      config: { ...sharedWorkPreferencesConfig }
    },
    {
      title: 'Low Compensation',
      description: currencyFormatter(writeUp?.minimum_salary),
      config: { ...sharedWorkPreferencesConfig }
    }
  ];

  const jobSituationLabels = [
    {
      title: 'Reason for leaving',
      description: writeUp?.reason_leaving,
      cropped: false,
      config: {
        sm: 12,
        md: 12
      }
    },
    {
      title: 'Time Looking for Job',
      description: lookingFor
    },
    { title: 'Ideal time to start', description: writeUp?.timeToStart?.title }
  ];

  const achievementsAndSkillsLabels = [
    {
      title: 'Achievements',
      description: (
        <>
          <span className={classes.achievementText}>
            <strong>1. </strong> {writeUp?.achievement_one}
          </span>
          <span className={classes.achievements}>
            <strong>2. </strong>
            {writeUp?.achievement_two}
          </span>
          <span className={classes.achievementText}>
            <strong>3. </strong>
            {writeUp?.achievement_three}
          </span>
        </>
      ),
      cropped: false,
      config: SharedFullGridLayout
    },
    {
      title: 'Skills',
      description: writeUp?.experience,
      cropped: false,
      config: SharedFullGridLayout
    }
  ];

  const dateInterviewsLabel = [
    {
      description: writeUp?.interview_dates?.[0] ? (
        <>
          <span>
            <strong>1. </strong>
            {localTimeFormatter(writeUp?.interview_dates?.[0], DateFormats.SendoutTemplateDate)}
          </span>
        </>
      ) : null,
      cropped: false,
      config: { sm: 4, md: 4 }
    },
    {
      description: writeUp?.interview_dates?.[1] ? (
        <>
          <strong>2. </strong>
          {localTimeFormatter(writeUp?.interview_dates?.[1], DateFormats.SendoutTemplateDate)}
        </>
      ) : null,
      cropped: false,
      config: { sm: 4, md: 4 }
    },
    {
      description: writeUp?.interview_dates?.[2] ? (
        <>
          <strong>3. </strong>
          {localTimeFormatter(writeUp?.interview_dates?.[2], DateFormats.SendoutTemplateDate)}
        </>
      ) : null,
      cropped: false,
      config: { sm: 4, md: 4 }
    }
  ];

  const legacyCards = () => {
    return [
      {
        cardProps: {
          headerProps: {
            title: 'Work Preferences'
          }
        },
        Component: InfoLabelsGrid,
        componentProps: {
          loading,
          isFlexibleGrid: true,
          labels: workPreferences
        }
      },
      {
        cardProps: {
          headerProps: {
            title: 'Current Job Situation'
          }
        },
        Component: InfoLabelsGrid,
        componentProps: {
          loading,
          isFlexibleGrid: true,
          labels: jobSituationLabels
        }
      },
      {
        cardProps: {
          headerProps: {
            title: 'Achievements & Skills'
          }
        },
        Component: InfoLabelsGrid,
        componentProps: {
          loading,
          isFlexibleGrid: true,
          labels: achievementsAndSkillsLabels
        }
      },
      {
        cardProps: {
          headerProps: {
            title: 'Available Date Interviews'
          }
        },
        Component: InfoLabelsGrid,
        componentProps: {
          loading,
          isFlexibleGrid: true,
          labels: dateInterviewsLabel
        }
      },
      writeUp?.notes
        ? {
            cardProps: {
              headerProps: {
                title: 'Additional Details'
              }
            },
            Component: InfoLabelsGrid,
            componentProps: {
              loading,
              isFlexibleGrid: true,
              labels: [
                {
                  description: writeUp?.notes,
                  config: { sm: 12, md: 12, style: { whiteSpace: 'pre-line' } }
                }
              ]
            }
          }
        : {
            cardProps: {
              headerProps: {
                title: 'Additional Details'
              }
            },
            Component: InfoLabelsGrid,
            componentProps: {
              loading,
              labels: [
                {
                  description: '',
                  config: { xs: 12, md: 12 }
                }
              ]
            }
          }
    ];
  };

  const createDataSheetCards = () => {
    return [
      {
        cardProps: {
          headerProps: {
            title: 'Establishing Urgency'
          }
        },
        id: CANDIDATES_PROFILE_HASHES.EstablishingUrgency,
        Component: InfoLabelsGrid,
        componentProps: {
          loading,
          isFlexibleGrid: true,
          labels: establishingUrgencyLabels(candidateDataSheet?.data?.establishingUrgency)
        }
      },
      {
        cardProps: {
          headerProps: {
            title: 'Candidate Background'
          }
        },
        Component: InfoLabelsGrid,
        componentProps: {
          loading,
          isFlexibleGrid: true,
          labels: candidateBackgroundLabels(candidateDataSheet?.data?.candidateBackground)
        }
      },
      {
        cardProps: {
          headerProps: {
            title: 'Work Preferences'
          }
        },
        Component: InfoLabelsGrid,
        componentProps: {
          loading,
          isFlexibleGrid: true,
          labels: workPreferencesLabels(candidateDataSheet?.data?.workPreferences)
        }
      },
      {
        cardProps: {
          headerProps: {
            title: 'Professional Insights'
          }
        },
        Component: InfoLabelsGrid,
        componentProps: {
          loading,
          isFlexibleGrid: true,
          labels: professionalInsights(candidateDataSheet?.data?.professionalInsights)
        }
      },
      {
        cardProps: {
          headerProps: {
            title: 'Notes'
          }
        },
        Component: InfoLabelsGrid,
        componentProps: {
          loading,
          isFlexibleGrid: true,
          labels: [
            {
              title: 'Notes',
              description: candidateDataSheet?.data?.qualification?.notes,
              config: { sm: 9, md: 9, xs: 9, style: { whiteSpace: 'pre-line' } }
            }
          ]
        }
      }
    ];
  };

  const finalCards = candidateDataSheet && success ? createDataSheetCards() : legacyCards();

  return (
    <>
      <DefaultCards
        profile={profile}
        loading={loading}
        onEditClick={onEditClick}
        onWriteUpClick={onWriteUpClick}
        candidateDataSheet={candidateDataSheet}
      />
      <InventoryOverviewLayout items={finalCards} loading={loading} />
    </>
  );
};

CandidateOverview.defaultProps = {
  profile: {},
  blueSheet: {}
};

export default CandidateOverview;
